import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import './AttendanceView.css';

const AttendanceView = () => {
  const [attendance, setAttendance] = useState([]);
  const [statistics, setStatistics] = useState({
    total_days: 0,
    present_days: 0,
    absent_days: 0,
    late_days: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get token from sessionStorage
      const token = sessionStorage.getItem('token');
      
      console.log(`📊 Fetching attendance for Month: ${selectedMonth}, Year: ${selectedYear}`);

      // Fetch attendance from backend employee API
      const url = `/api/employee/attendance?month=${selectedMonth}&year=${selectedYear}`;
      console.log('🌐 API URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      console.log('📥 API Response:', data);
      
      if (response.ok) {
        setAttendance(data.attendance || []);
        setStatistics(data.statistics || {
          total_days: 0,
          present_days: 0,
          absent_days: 0,
          late_days: 0
        });
        console.log('✅ Attendance data loaded:', data.attendance?.length || 0, 'records');
        console.log('👤 Employee info:', data.employeeInfo);
      } else {
        console.error('❌ Failed to fetch attendance:', data.error);
        toast.error('Failed to fetch attendance: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Failed to fetch attendance:', error);
      toast.error('Error fetching attendance: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const getStatusIcon = (status, timeStatus) => {
    if (status === 'present') {
      return timeStatus === 'late' ? 
        <AlertCircle size={16} className="status-icon late" /> :
        <CheckCircle size={16} className="status-icon present" />;
    }
    return <XCircle size={16} className="status-icon absent" />;
  };

  if (loading) {
    return <div className="loading-spinner">Loading attendance...</div>;
  }

  return (
    <div className="attendance-view">
      <div className="attendance-header">
        <h3>Attendance History</h3>
        <div className="date-filters">
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {Array.from({length: 12}, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {format(new Date(2000, i, 1), 'MMMM')}
              </option>
            ))}
          </select>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {Array.from({length: 5}, (_, i) => {
              const year = new Date().getFullYear() - 2 + i;
              return (
                <option key={year} value={year}>{year}</option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="attendance-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-info">
            <h4>{statistics.total_days}</h4>
            <p>Total Days</p>
          </div>
        </div>

        <div className="stat-card present">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-info">
            <h4>{statistics.present_days}</h4>
            <p>Present Days</p>
          </div>
        </div>

        <div className="stat-card absent">
          <div className="stat-icon">
            <XCircle size={24} />
          </div>
          <div className="stat-info">
            <h4>{statistics.absent_days}</h4>
            <p>Absent Days</p>
          </div>
        </div>

        <div className="stat-card late">
          <div className="stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="stat-info">
            <h4>{statistics.late_days}</h4>
            <p>Late Days</p>
          </div>
        </div>
      </div>

      <div className="attendance-table-container">
        <div className="attendance-table">
          {attendance.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Status</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Total Hours</th>
                  <th>Late (min)</th>
                  <th>Shift</th>
                </tr>
              </thead>
              <tbody>
                {attendance.map((record, index) => {
                  // Map attendance_summary fields from backend
                  const status = (record.status || '').toLowerCase();
                  const isPresent = status === 'present' || status === 'p';
                  const isAbsent = status === 'absent' || status === 'a';
                  const timeStatus = (record.late_status || '').toLowerCase();
                  const isLate = timeStatus === 'late' || timeStatus === 'l';
                  
                  return (
                    <tr key={index}>
                      <td>{record.date ? format(new Date(record.date), 'MMM dd, yyyy') : '-'}</td>
                      <td>{record.date ? format(new Date(record.date), 'EEEE') : '-'}</td>
                      <td>
                        <div className="status-cell">
                          {getStatusIcon(isPresent ? 'present' : 'absent', isLate ? 'late' : 'on_time')}
                          <span className={`status ${isPresent ? 'present' : 'absent'}`}>
                            {isPresent ? 'Present' : isAbsent ? 'Absent' : status}
                          </span>
                        </div>
                      </td>
                      <td>{record.first_in || '-'}</td>
                      <td>{record.last_out || '-'}</td>
                      <td>{record.total_hours ? parseFloat(record.total_hours).toFixed(2) : '-'}</td>
                      <td>
                        <span className={`time-status ${isLate ? 'late' : 'on_time'}`}>
                          {isLate ? 'Late' : 'On Time'}
                        </span>
                      </td>
                      <td>{record.shift_start || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              <Calendar size={48} />
              <p>No attendance records found for the selected period.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceView;